import TokenAuthApi from '@/api/tokenAuthApi';
import moment from 'moment';
import UserSessionService from '@/services/userSessionService';
import SessionApi from '@/api/sessionApi';
import TenantSessionService from '@/services/tenantSessionService';

const userSession = UserSessionService.getSession();

const state = {
  token: userSession.token || null,
  user: userSession.user || null,
  session: userSession.session || null,
  tenant: userSession.tenant || null,
  impersonation: {
    isImpersonated: {
      value: userSession.impersonated,
      loading: false
    },
    availableTenants: {
      items: [],
      loading: false,
      canSearchTenants: false
    },
    availableUsers: {
      items: [],
      loading: false
    }
  }
};

const getters = {
  isAuthenticated: (state, getters) => {
    if (!state.session) {
      return false;
    }
    return getters.sessionExpired;
  },
  token: state => state.token,
  sessionExpired: state => {
    return !state.session || !state.session.expire || state.session.expire.isAfter(moment());
  },
  permissions: state => {
    if (!state.user) {
      return [];
    }

    return state.user.permissions;
  },
  roles: state => {
    if (!state.user) {
      return [];
    }

    return state.user.roles;
  },
  canViewManagePanel: (state, getters) => {
    if (!state.user) {
      return false;
    }

    for (const permission of getters.permissions) {
      if (permission.startsWith('Lms_Administration')) {
        return true;
      }
    }

    return false;
  },
  canViewHomePage: (state, getters) => {
    if (!state.user) {
      return false;
    }

    for (const permission of getters.permissions) {
      if (permission.startsWith('Lms_ExecuteCourses')) {
        return true;
      }
    }

    return false;
  }
};

const actions = {
  async signIn({ dispatch, commit }, payload) {
    try {
      const { data: authData } = await new TokenAuthApi(null).authenticate({
        userNameOrEmailAddress: payload.email,
        password: payload.password,
        rememberClient: false,
        tenancyName: payload.tenancyName
      });

      if (payload.tenancyName && payload.tenancyName !== 'host') {
        commit('USER_LAST_TENANT', { lastTenant: payload.tenancyName });
      }

      return await dispatch('createSession', {
        accessToken: authData.result.accessToken,
        expireInSeconds: authData.result.expireInSeconds,
        userId: authData.result.userId,
        impersonated: false
      });
    } catch (e) {
      // TODO: handle
      console.error(e);
      return false;
    }
  },
  async createSession({ commit }, payload) {
    try {
      const { data: sessionData } = await new SessionApi(payload.accessToken).getCurrentLoginInformations();

      // TODO: add setTimeout to expired session??
      commit('SAVE_USER_SESSION', {
        token: payload.accessToken,
        session: {
          token: payload.accessToken,
          expireInSeconds: payload.expireInSeconds,
          expire: moment().add(payload.expireInSeconds, 'seconds'),
          userId: payload.userId
        },
        user: sessionData.result.user,
        tenant: sessionData.result.tenant,
        impersonated: payload.impersonated
      });

      return true;
    } catch (e) {
      // TODO: handle
      console.error(e);
      return false;
    }
  },
  singOut({ commit }) {
    commit('CLEAR_USER_SESSION');
  },
  async loadAvailableTenantsForImpersonation({ commit, state }, payload) {
    commit('CHANGE_AVAILABLE_TENANTS_LOADING', { loading: true });

    try {
      const { data } = await new TokenAuthApi(state.token).getAvailableTenantsForImpersonation(payload.keyword);

      let tenants = data.result;
      let canSearchTenants = false;
      if (tenants.length > 1 && tenants[0].id === 0 && tenants[1].id === null) {
        tenants = tenants.map(tenant => {
          if (tenant.id === 0) {
            return {
              ...tenant,
              displayName: '[Wszystkie tenanty]'
            };
          } else if (tenant.id === null) {
            return {
              ...tenant,
              id: 'null',
              displayName: '[Host]'
            };
          }

          return {
            ...tenant,
            displayName: tenant.name
          };
        });
        canSearchTenants = true;
      }

      commit('SAVE_AVAILABLE_TENANTS', {
        tenants: tenants,
        canSearchTenants: canSearchTenants
      });
    } catch (e) {
      console.error(e);
    } finally {
      commit('CHANGE_AVAILABLE_TENANTS_LOADING', { loading: false });
    }
  },
  async loadAvailableUsersForImpersonation({ commit, state }, payload) {
    commit('CHANGE_AVAILABLE_USERS_LOADING', { loading: true });

    try {
      const { data } = await new TokenAuthApi(state.token).getAvailableUsersForImpersonation(
        payload.keyword,
        payload.tenantId
      );

      const users = data.result.map(item => ({
        ...item,
        displayName: `${item.fullName} (${item.emailAddress})`
      }));
      commit('SAVE_AVAILABLE_USERS', { users: users });
    } catch (e) {
      console.error(e);
    } finally {
      commit('CHANGE_AVAILABLE_USERS_LOADING', { loading: false });
    }
  },
  async impersonate({ commit, state, dispatch }, payload) {
    commit('IMPERSONATE_USER_LOADING', { loading: true });

    try {
      const { data } = await new TokenAuthApi(state.token).impersonate(payload.targetUserId, payload.targetTenantId);

      await dispatch('createSession', {
        accessToken: data.result.accessToken,
        expireInSeconds: data.result.expireInSeconds,
        userId: data.result.userId,
        impersonated: true
      });
    } catch (e) {
      console.error(e);
    } finally {
      commit('IMPERSONATE_USER_LOADING', { loading: false });
    }
  },
  async unImpersonate({ commit, state, dispatch }) {
    commit('IMPERSONATE_USER_LOADING', { loading: true });

    try {
      const { data } = await new TokenAuthApi(state.token).unImpersonate();

      await dispatch('createSession', {
        accessToken: data.result.accessToken,
        expireInSeconds: data.result.expireInSeconds,
        userId: data.result.userId,
        impersonated: false
      });
    } catch (e) {
      console.error(e);
    } finally {
      commit('IMPERSONATE_USER_LOADING', { loading: false });
    }
  }
};

const mutations = {
  SAVE_USER_SESSION(state, payload) {
    UserSessionService.saveSession(payload.token, payload.session, payload.user, payload.tenant, payload.impersonated);

    state.token = payload.token;
    state.session = payload;
    state.user = payload.user;
    state.tenant = payload.tenant;

    state.impersonation = {
      ...state.impersonation,
      isImpersonated: {
        ...state.impersonation.isImpersonated,
        value: payload.impersonated
      }
    };
  },
  CLEAR_USER_SESSION(state) {
    UserSessionService.removeSession();

    state.token = null;
    state.session = null;
    state.user = null;
    state.tenant = null;
  },
  CHANGE_AVAILABLE_TENANTS_LOADING(state, payload) {
    state.impersonation = {
      ...state.impersonation,
      availableTenants: {
        ...state.impersonation.availableTenants,
        loading: payload.loading
      }
    };
  },
  SAVE_AVAILABLE_TENANTS(state, payload) {
    state.impersonation = {
      ...state.impersonation,
      availableTenants: {
        ...state.impersonation.availableTenants,
        items: payload.tenants,
        canSearchTenants: payload.canSearchTenants
      }
    };
  },
  CHANGE_AVAILABLE_USERS_LOADING(state, payload) {
    state.impersonation = {
      ...state.impersonation,
      availableUsers: {
        ...state.impersonation.availableUsers,
        loading: payload.loading
      }
    };
  },
  SAVE_AVAILABLE_USERS(state, payload) {
    state.impersonation = {
      ...state.impersonation,
      availableUsers: {
        ...state.impersonation.availableUsers,
        items: payload.users
      }
    };
  },
  IMPERSONATE_USER_LOADING(state, payload) {
    state.impersonation = {
      ...state.impersonation,
      isImpersonated: {
        ...state.impersonation.isImpersonated,
        loading: payload.loading
      }
    };
  },
  IMPERSONATE_USER(state) {
    state.impersonation = {
      ...state.impersonation,
      isImpersonated: {
        ...state.impersonation.isImpersonated,
        value: true
      }
    };
  },
  CLEAR_IMPERSONATED_USER(state) {
    state.impersonation = {
      ...state.impersonation,
      isImpersonated: {
        ...state.impersonation.isImpersonated,
        value: false
      }
    };
  },
  USER_LAST_TENANT(_, payload) {
    TenantSessionService.saveLastTenant(payload.lastTenant);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
